import { useEffect, useState } from 'react'
import { fxSuppliersApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

type SupplierType = 'producto' | 'servicio'
type ServiceCategory = 'marketing' | 'logistica' | 'contabilidad' | 'otro'

const SERVICE_CATEGORY_LABELS: Record<ServiceCategory, string> = {
  marketing: 'Marketing',
  logistica: 'Logística',
  contabilidad: 'Contabilidad',
  otro: 'Otro',
}

interface Supplier {
  id: number
  name: string
  contact_name: string | null
  phone: string | null
  email: string | null
  address: string | null
  ruc: string | null
  notes: string | null
  supplier_type: SupplierType
  service_category: ServiceCategory | null
  is_active: boolean
  created_at: string
}

interface SupplierForm {
  name: string
  contact_name: string
  phone: string
  email: string
  address: string
  ruc: string
  notes: string
  supplier_type: SupplierType
  service_category: ServiceCategory | ''
}

const emptyForm: SupplierForm = {
  name: '', contact_name: '', phone: '', email: '', address: '', ruc: '', notes: '',
  supplier_type: 'producto', service_category: '',
}

export default function FxSuppliersPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<SupplierForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [filterType, setFilterType] = useState<'' | SupplierType>('')

  const fetchSuppliers = async () => {
    try {
      const res = await fxSuppliersApi.list()
      setSuppliers(res.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching FX suppliers:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSuppliers()
  }, [])

  const filtered = filterType ? suppliers.filter((s) => s.supplier_type === filterType) : suppliers

  const { sortedData, sortConfig, requestSort } = useSortableData(filtered, {
    key: 'name',
    direction: 'asc',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setForm((prev) => ({
      ...prev,
      [name]: value,
      // Switching back to "producto" clears any previously chosen service
      // category -- it only makes sense (and is only accepted server-side)
      // for a "servicio" supplier.
      ...(name === 'supplier_type' && value === 'producto' ? { service_category: '' } : {}),
    }))
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (s: Supplier) => {
    setForm({
      name: s.name,
      contact_name: s.contact_name || '',
      phone: s.phone || '',
      email: s.email || '',
      address: s.address || '',
      ruc: s.ruc || '',
      notes: s.notes || '',
      supplier_type: s.supplier_type,
      service_category: s.service_category || '',
    })
    setEditingId(s.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    if (form.supplier_type === 'servicio' && !form.service_category) {
      setError('Selecciona el tipo de servicio')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        ...form,
        contact_name: form.contact_name || null,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        ruc: form.ruc || null,
        notes: form.notes || null,
        service_category: form.service_category || null,
      }
      if (editingId) {
        await fxSuppliersApi.update(editingId, payload)
      } else {
        await fxSuppliersApi.create(payload)
      }
      setShowForm(false)
      fetchSuppliers()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar proveedor')
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (s: Supplier) => {
    try {
      await fxSuppliersApi.update(s.id, { is_active: !s.is_active })
      fetchSuppliers()
    } catch (err) {
      console.error('Error updating supplier status:', err)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Proveedores — FX Admin</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Agregar Proveedor
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Proveedor' : 'Nuevo Proveedor'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Nombre *</label>
                <input
                  type="text" name="name" value={form.name} onChange={handleChange} required
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Tipo *</label>
                  <select
                    name="supplier_type" value={form.supplier_type} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="producto">Producto</option>
                    <option value="servicio">Servicio</option>
                  </select>
                </div>
                {form.supplier_type === 'servicio' && (
                  <div>
                    <label className="block text-sm font-medium text-espresso-700 mb-1">Tipo de Servicio *</label>
                    <select
                      name="service_category" value={form.service_category} onChange={handleChange} required
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="">Selecciona un tipo</option>
                      {(Object.keys(SERVICE_CATEGORY_LABELS) as ServiceCategory[]).map((key) => (
                        <option key={key} value={key}>{SERVICE_CATEGORY_LABELS[key]}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {form.supplier_type === 'servicio' && (
                <p className="text-xs text-espresso-500 -mt-2">
                  Los proveedores de servicios no aparecen al registrar una compra de stock, solo en gastos.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Contacto</label>
                  <input
                    type="text" name="contact_name" value={form.contact_name} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Teléfono</label>
                  <input
                    type="text" name="phone" value={form.phone} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Email</label>
                  <input
                    type="email" name="email" value={form.email} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">RUC</label>
                  <input
                    type="text" name="ruc" value={form.ruc} onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Dirección</label>
                <textarea
                  name="address" value={form.address} onChange={handleChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Notas</label>
                <textarea
                  name="notes" value={form.notes} onChange={handleChange} rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50">
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {suppliers.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay proveedores aún. Agrega el primero para comenzar.</p>
        </div>
      ) : (
        <>
          <div className="bg-cream-50 shadow rounded-lg p-4 mb-4 flex items-center gap-3">
            <label className="text-xs font-medium text-espresso-500">Tipo</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as '' | SupplierType)}
              className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            >
              <option value="">Todos</option>
              <option value="producto">Productos</option>
              <option value="servicio">Servicios</option>
            </select>
          </div>
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Tipo" sortKey="supplier_type" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Contacto" sortKey="contact_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Teléfono" sortKey="phone" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="RUC" sortKey="ruc" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedData.map((s) => (
                  <tr key={s.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{s.name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {s.supplier_type === 'servicio'
                        ? `Servicio${s.service_category ? ` — ${SERVICE_CATEGORY_LABELS[s.service_category]}` : ''}`
                        : 'Producto'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{s.contact_name || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{s.phone || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{s.ruc || '-'}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${s.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {s.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(s)} variant="primary">Editar</TableActionButton>
                        <TableActionButton onClick={() => toggleActive(s)} variant={s.is_active ? 'danger' : 'primary'}>
                          {s.is_active ? 'Desactivar' : 'Activar'}
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </div>
  )
}
